import { Component, inject, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DocumentDialogComponent } from '@components/document-dialog';
import { Debt, Document } from '@models/matter';
import { GroupType } from '@models/documents';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { DebtsDispatch, DebtsState } from '@state/matter/debts';
import { DocumentsDispatch } from '@state/matter/documents';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { combineLatest, Observable } from 'rxjs';
import { DebtDialogComponent } from './debt-dialog';
import { ICON } from '@core/util/icon';
import { MergedMatterDocumentSelectors } from '@state/global/documents/merged-documents';
import { getFolderHashedId } from '@core/util/documents';
import { TestHooks } from './debts.hooks';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

export interface DebtData {
	debtId: string;
	matterId: string;
}

@UntilDestroy()
@Component({
	selector: 'app-debts',
	templateUrl: './debts.component.html',
	styleUrls: ['./debts.component.scss'],
	standalone: false,
})
export class DebtsComponent implements OnInit {
	testHooks = TestHooks;
	drawerService = inject(DrawerService);

	@Select(SelectedMatter.useHtmlDocuments)
	public useHtmlDocuments$: Observable<boolean>;
	@Select(DebtsState.getAll) debts$: Observable<Debt[]>;
	documents: { [key: string]: Document[] };
	@Select(MergedMatterDocumentSelectors.getDocumentsByFolderId)
	documentsGroupFilterFn$: Observable<(id: string) => Document[]>;

	readonly ICON = ICON;
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		private debtsDispatch: DebtsDispatch,
		private dialog: MatDialog,
		private documentsDispatch: DocumentsDispatch,
	) {
		this.documents = {};
	}

	ngOnInit(): void {
		combineLatest([this.debts$, this.documentsGroupFilterFn$])
			.pipe(untilDestroyed(this))
			.subscribe(([debts, filterFn]) => {
				debts.forEach((debt) => {
					this.documents[debt.id] = filterFn(
						getFolderHashedId(this.matterId, GroupType.DEBT, debt.id, true),
					);
				});
			});
	}

	addDebt(): void {
		this.openDebtDialog();
	}

	debtId(_, debt: Debt): string {
		return debt.id;
	}

	documentId(_, document: Document): string {
		return document.id;
	}

	editDebt(debt: Debt): void {
		this.openDebtDialog(debt.id);
	}

	openDocument(
		documentId: string,
		groupId: string,
		title: string = '',
		payee: string = '',
	): void {
		this.drawerService.openDrawer(DocumentDialogComponent, {
			title: [title, payee].filter((segment) => !!segment).join(': '),
			data: {
				documentId,
			},
			noStyle: true,
		});
	}

	removeDebt(debt: Debt): void {
		this.debtsDispatch.Remove(debt.id);
		this.documents[debt.id].forEach((document) =>
			this.documentsDispatch.Remove(document.id),
		);
	}

	private openDebtDialog(debtId: string = ''): void {
		this.dialog.open(DebtDialogComponent, {
			autoFocus: true,
			data: {
				debtId,
				matterId: this.matterId,
			},
		});
	}
}
